import { useSnackbar } from "notistack";
import { FilteringCategory } from "../../../../../domain/model/Category";
import { useEffect, useState } from "react";
import { State } from "../../../../../domain/model/ResponseState";
import { DeleteQuestionUseCase } from "../../../../../domain/usecase/question/delete/DeleteUseCase";
import { DeleteAnswerByQuestionIdUseCase } from "../../../../../domain/usecase/answer/deleteByQuestionId/DeleteByQuestionIdUseCase";
import { GetAllCategoriesUseCase } from "../../../../../domain/usecase/category/getAll/GetAllUseCase";
import { FilterQuestionsByUseCase } from "../../../../../domain/usecase/question/filter/FilterUseCase";
import { Answer, Question } from "../../../../../domain/model/Question";
import { GetAllQuestionsUseCase } from "../../../../../domain/usecase/question/getAll/GetAllUseCase";
import { useNavigate } from "react-router-dom";
import { GetAnswersByListOfQuestionsIdUseCase } from "../../../../../domain/usecase/answer/getAllByListOfQuestionsId /UseCase";
import { GetQuestionsAnswersStatsUseCase } from "../../../../../domain/usecase/stats/questionsAnswers/UseCase";
import { QuestionAnswerCount } from "../../../../../domain/model/Stats";

export function QuestionsViewModel(
  filterQuestionsByUseCase: FilterQuestionsByUseCase,
  deleteQuestionUseCase: DeleteQuestionUseCase,
  deleteAnswerByQuestionIdUseCase: DeleteAnswerByQuestionIdUseCase,
  getAllCategoriesUseCase: GetAllCategoriesUseCase,
  getAllQuestionsUseCase: GetAllQuestionsUseCase,
  getAnswersByListOfQuestionsIdUseCase: GetAnswersByListOfQuestionsIdUseCase,
  getQuestionsAnswersStatsUseCase: GetQuestionsAnswersStatsUseCase,
) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allQuestionsLoading, setAllQuestionsLoading] = useState(false);
  const [isBackdropLoading, setIsBackdropLoading] = useState(false);
  const [categories, setCategories] = useState<
    Array<FilteringCategory> | undefined
  >([]);
  const [allQuestions, setAllQuestions] = useState<Array<Question> | undefined>(
    [],
  );
  const [allAnswers, setAllAnswers] = useState<Array<Answer> | undefined>([]);

  const [questionsAnswersCount, setQuestionsAnswersCount] =
    useState<QuestionAnswerCount>();

  const [skip, setSkip] = useState(0);
  const limit = 40;

  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
    fetchAllQuestions();
    getQuestionsAnswersCount();
  }, []);

  useEffect(() => {
    if (allAnswers && allAnswers.length > 0 && allQuestions) {
      allQuestions.map((q) => {
        if (allAnswers.filter((a) => a.question == q._id).length > 0) {
          q.answerTitle = allAnswers.filter(
            (a) => a.question == q._id,
          )[0].title;
        }
        return q;
      });
      setAllQuestions([...allQuestions]);
    }
  }, [allAnswers]);

  async function fetchAllQuestions() {
    if (skip == 0) setAllQuestionsLoading(true);
    else setIsLoadingMore(true);
    const response = await getAllQuestionsUseCase.invoke(skip, limit);
    setIsLoadingMore(false);
    setAllQuestionsLoading(false);
    switch (response.responseState) {
      case State.Success:
        if (!response.data) return;
        if (response.data.length < limit) setShowLoadMoreButton(false);
        else setShowLoadMoreButton(true);
        setAllQuestions(allQuestions?.concat(response.data));
        setSkip(skip + limit);
        fetchAnswersByListOfQuestionsId(response.data);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function fetchAnswersByListOfQuestionsId(
    questionsList: Array<Question>,
  ): Promise<Array<Answer> | undefined> {
    if (questionsList.length == 0) return;
    const response = await getAnswersByListOfQuestionsIdUseCase.invoke(
      questionsList.map((q) => q._id).join(","),
    );
    switch (response.responseState) {
      case State.Success:
        if (!response.data) return;
        const answers = response.data;
        setAllAnswers(answers);
        return answers;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function fetchQuestionsOfCategory(categoryId: string) {
    const response = await filterQuestionsByUseCase.invoke(
      `categoryId=${categoryId}`,
    );
    switch (response.responseState) {
      case State.Success:
        if (!response.data || !categories) return;
        const questions = response.data;
        if (questions.length > 0) {
          const answers = await fetchAnswersByListOfQuestionsId(questions);
          if (answers) {
            questions.map((q) => {
              if (answers.filter((a) => a.question == q._id).length > 0) {
                q.answerTitle = answers.filter(
                  (a) => a.question == q._id,
                )[0].title;
              }
              return q;
            });
          }
        }

        categories.map((c) => {
          if (c._id === categoryId) {
            c.questions = questions;
          }
        });
        setCategories([...categories]);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function deleteQuestion(id: string) {
    setIsBackdropLoading(true);
    const response = await deleteQuestionUseCase.invoke(id);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        await deleteAnswerByQuestionId(id);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        setIsBackdropLoading(false);
        break;
    }
  }

  async function deleteAnswerByQuestionId(id: string) {
    const response = await deleteAnswerByQuestionIdUseCase.invoke(id);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        navigate(0);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setIsBackdropLoading(false);
  }

  async function fetchCategories() {
    setLoading(true);
    const response = await getAllCategoriesUseCase.invoke();
    switch (response.responseState) {
      case State.Success:
        const tmpList: Array<FilteringCategory> = [];
        response.data?.map((category) => {
          const tmpFilteringCategory: FilteringCategory = {
            _id: category._id,
            title: category.title,
            isSelected: false,
          };
          tmpList.push(tmpFilteringCategory);
        });
        setCategories(tmpList);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
    setLoading(false);
  }

  async function filterQuestions() {}

  function hasNoFilter(): boolean {
    return getSelectedCategoriesList().length == 0;
  }

  async function getQuestionsAnswersCount() {
    const response = await getQuestionsAnswersStatsUseCase.invoke();
    switch (response.responseState) {
      case State.Success:
        if (!allQuestions) return;
        if (!response.data) return;
        setQuestionsAnswersCount(response.data);

        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function getSelectedCategoriesList(): Array<FilteringCategory> {
    if (!categories) return [];
    return categories?.filter((c) => c.isSelected);
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error",
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }

  return {
    allQuestions,
    isBackdropLoading,
    isLoading,
    deleteQuestion,
    showLoadMoreButton,
    setLoading,
    filterQuestions,
    hasNoFilter,
    getSelectedCategoriesList,
    categories,
    fetchQuestionsOfCategory,
    allQuestionsLoading,
    fetchAllQuestions,
    questionsAnswersCount,
    isLoadingMore,
  };
}
